import React, { useState, useEffect } from 'react';
import moment from 'moment'
import DatePicker from 'react-datepicker';
import { Button, Checkbox, TextField, Typography } from '@material-ui/core';
import { CreateCartoMapContext } from '../../../contexts/carto-maps-context'
import FromProjectCard from './components/FromProjectCard'
import ToProjectCard from './components/ToProjectCard'
import ConfirmationModal from './components/ConfirmationModal'
import AlertModal from './components/AlertModal'
import api from '../../../../services/api'
import { MAX_ZONES_ALLOWED_IN_MAP } from './constants'
import "react-datepicker/dist/react-datepicker.css";
import './style.css';

function CopyZones(props) {
  const [ fromProjectZones, setFromProjectZones ] = useState([])
  const [ zonesToAddToMap, setZonesToAddToMap ] = useState([])
  const [ isModalOpen, setIsModalOpen ] = useState(false);
  const [ projects, setProjects ] = useState([])
  const [ selectedFromProject, setSelectedFromProject ] = useState('')
  const [ fromDisplayedZones, setFromDisplayedZones ] = useState([])
  const [ zonesInProjectCount, setZonesInProjectCount ] = useState(0)
  const [ visualisationName, setVisualisationName ] = useState('')
  const [ fromDate, setFromDate ] = useState(new Date(moment().utc(false).startOf('day').subtract(1, 'week')))
  const [ toDate, setToDate ] = useState(new Date(moment().utc(false).endOf('day')))
  const [ isAllTimeSelected, setIsAllTimeSelected ] = useState(false)
  const [ isAlertModalOpen, setIsAlertModalOpen ] = useState(false)
  const accessToken = localStorage.getItem("idToken")

  // GET PROJECTS WHEN ACCESSTOKEN IS UPDATED
  useEffect(() => {
    const accountId = props.id

    async function fetchProjects() {
      try {
        if (accessToken) {
          const projects = await api.getProjects(accountId, accessToken)
          setProjects(projects)
        }
      } catch (error) {
        console.log('error', error)
        alert('There was an issue loading the Projects')
      }
    }

    fetchProjects()
  }, [accessToken])

  // LOAD ZONES WHEN SELECTED PROJECT CHANGES
  useEffect(() => {
    async function fetchSelectedFromProjectZones() {
      try {
        if (selectedFromProject._id) {
          const getZonesQueryParams = { limit: MAX_ZONES_ALLOWED_IN_MAP }
          const zonesResponse = await api.getZones(selectedFromProject._id, accessToken, getZonesQueryParams)
          const zones = zonesResponse.data
          setFromProjectZones(zones)
          setZonesToAddToMap([])
        }
      } catch (error) {
        console.log('error', error)
        alert('There was an issue loading the Zones')
      }
    }

    async function getZonesInProjectCount () {
      try {
        if (selectedFromProject._id) {
          const { count } = await api.getZonesCount(selectedFromProject._id, accessToken)
          setZonesInProjectCount(count)
        } 
      } catch (error) {
        console.log('error', error)
        alert('There was an issue getting the count of Zones in this Project')
      }
    }

    fetchSelectedFromProjectZones()
    getZonesInProjectCount()
  }, [selectedFromProject])

  useEffect(() => {
    if (zonesInProjectCount > MAX_ZONES_ALLOWED_IN_MAP) {
      setIsAlertModalOpen(true)
    }
  }, [zonesInProjectCount, selectedFromProject])

  const resetForm = () => {
    setFromProjectZones([])
    setZonesToAddToMap([])
    setSelectedFromProject('')
    setZonesInProjectCount(0)
    setVisualisationName('')
  }

  const isFormValid = () => {
     if (!visualisationName.trim()) return false
     if (fromDate > toDate) return false
     if (!selectedFromProject) return false
     if (zonesToAddToMap.length === 0) return false
     return true
  }

  const handleFromDateChange = (date) => {
    const newFromDate = new Date(moment(date).utc(false).startOf('day'))
    setFromDate(newFromDate)
  }

  const handleToDateChange = (date) => {
    const newToDate = new Date(moment(date).utc(false).endOf('day'))
    setToDate(newToDate)
  }

  const handleVisualisationNameChange = (event) => {
    const name = event.target.value
    const nameWithoutSpecialChars = name.replace(/[^\w\s]/gi, '');
    setVisualisationName(nameWithoutSpecialChars)
  }

  const value = {
    accountId: props.id,

    visualisationName,
    setVisualisationName,

    fromDate,
    toDate,
    isAllTimeSelected,

    fromProject: {
      zones: fromProjectZones,
      setZones: setFromProjectZones
    },
    zonesSelected: {
      zones: zonesToAddToMap,
      setZones: setZonesToAddToMap,
    },

    projects,

    selectedFromProject,
    setSelectedFromProject,

    fromDisplayedZones,
    setFromDisplayedZones,

    resetForm
  }

  return (
    <CreateCartoMapContext.Provider value={value}>
        <div className="create-map-container">
          <label className="box-label">Create Carto Map Visualisation</label>
          <Typography variant="h5">1. Add a name</Typography>
          <div className="step-section-wrapper">
              <h4>Visualisation name: </h4>
              <TextField
                label="Visualisation name"
                id="vis-name"
                required
                variant="outlined"
                value={visualisationName}
                onChange={handleVisualisationNameChange}
              />
          </div>
          <Typography variant="h5">2. Select date range (UTC)</Typography>
          <div className="dates-selection-wrapper">
            <div className="step-section-wrapper">
              <h4>From date: </h4>
              <DatePicker 
                className="form-control" 
                selected={fromDate} 
                onChange={handleFromDateChange} 
                disabled={isAllTimeSelected}
              />
              <h4>To date: </h4>
              <DatePicker 
                className="form-control" 
                selected={toDate} 
                onChange={handleToDateChange} 
                disabled={isAllTimeSelected} 
              />
              <h4>All time</h4>
              <Checkbox color="primary" checked={isAllTimeSelected} onChange={e => setIsAllTimeSelected(e.target.checked)} />
            </div>
          </div>
          
          <Typography variant="h5">3. Select Project and Zones</Typography>
          <div className="cards-container">
            <FromProjectCard />
            <ToProjectCard />
          </div>
          <div className="confirm-button-wrapper">
            <Button 
              variant="contained" 
              color="primary" 
              disableElevation
              onClick={() => setIsModalOpen(true)}
              disabled={!isFormValid()}
            >
              Create Visualisation
            </Button>
          </div>
        </div>
        <ConfirmationModal isOpen={isModalOpen} setIsOpen={setIsModalOpen}/>
        <AlertModal isOpen={isAlertModalOpen} onClose={() => setIsAlertModalOpen(false)}></AlertModal>
    </CreateCartoMapContext.Provider>
  );
}

export default CopyZones;
